import get from 'lodash.get';
import isEqual from 'lodash.isequal';
import groupBy from 'lodash.groupby';
import mapValues from 'lodash.mapvalues';
import sumBy from 'lodash.sumby';

// Global Types
import { AppState } from 'services/types';

// Global Utils
import createDeepEqualSelector from 'utils/reselect/typescript/createDeepEqualSelector';
import stateDiff from 'utils/state-diff';

// Local Types
import { MyTimesheetEntryDate, MyTimesheetEntries } from './types';

export interface SelectorProps {
    myTimesheetEntryUuid: string;
}

const selectMyTimesheetEntriesReadOnlyValues = (
    state: AppState
): MyTimesheetEntries => state.myTimesheetEntries.readOnlyValues;

const selectMyTimesheetEntriesInitialValues = (
    state: AppState
): MyTimesheetEntries => state.myTimesheetEntries.initialValues;

const selectMyTimesheetEntriesValues = (state: AppState): MyTimesheetEntries =>
    state.myTimesheetEntries.values;

const selectMyTimesheetEntryUuid = (
    state: AppState,
    props: SelectorProps
): string => props.myTimesheetEntryUuid;

const selectMyTimesheetEntries = createDeepEqualSelector(
    [selectMyTimesheetEntriesReadOnlyValues, selectMyTimesheetEntriesValues],
    (myTimesheetEntriesReadOnlyValuesState, myTimesheetEntriesValuesState) => ({
        ...myTimesheetEntriesReadOnlyValuesState,
        ...myTimesheetEntriesValuesState
    })
);

const selectMyTimesheetEntriesArray = createDeepEqualSelector(
    selectMyTimesheetEntries,
    (myTimesheetEntriesState) => Object.values(myTimesheetEntriesState)
);

const selectMyTimesheetEntriesGroupedByDate = createDeepEqualSelector(
    selectMyTimesheetEntriesArray,
    (myTimesheetEntriesArrayState) =>
        groupBy(myTimesheetEntriesArrayState, 'date')
);

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectMyTimesheetEntriesByDate = (date: MyTimesheetEntryDate) =>
    createDeepEqualSelector(
        selectMyTimesheetEntriesGroupedByDate,
        (myTimesheetEntriesGroupedByDateState) =>
            get(myTimesheetEntriesGroupedByDateState, date)
    );

const selectMyTimesheetEntriesDateTotals = createDeepEqualSelector(
    selectMyTimesheetEntriesGroupedByDate,
    (myTimesheetEntriesGroupedByDateState) =>
        mapValues(myTimesheetEntriesGroupedByDateState, (myTimesheetEntries) =>
            sumBy(myTimesheetEntries, 'totalPaidMinutes')
        )
);

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectMyTimesheetEntriesDateTotal = (date: MyTimesheetEntryDate) =>
    createDeepEqualSelector(
        selectMyTimesheetEntriesDateTotals,
        (myTimesheetEntriesDateTotalsState) =>
            get(myTimesheetEntriesDateTotalsState, date)
    );

const selectUnsavedMyTimesheetEntriesExist = createDeepEqualSelector(
    [selectMyTimesheetEntriesInitialValues, selectMyTimesheetEntriesValues],
    (myTimesheetEntriesInitialValuesState, myTimesheetEntriesValuesState) =>
        !isEqual(
            myTimesheetEntriesInitialValuesState,
            myTimesheetEntriesValuesState
        )
);

const fieldWhitelist = [
    'date',
    'endTime',
    'entityId',
    'hasEditedPaidBreak',
    'paidBreakMinutes',
    'rate',
    'startTime',
    'units',
    'hasEditedUnpaidBreak',
    'unpaidBreakMinutes',
    'uuid',
    'workTypeId',
    'groupId'
];

const selectUnsavedMyTimesheetEntries = createDeepEqualSelector(
    [selectMyTimesheetEntriesInitialValues, selectMyTimesheetEntriesValues],
    (myTimesheetEntriesInitialValuesState, myTimesheetEntriesValuesState) =>
        stateDiff(
            myTimesheetEntriesInitialValuesState,
            myTimesheetEntriesValuesState,
            fieldWhitelist
        )
);

const selectMyTimesheetEntryInitialValue = createDeepEqualSelector(
    [selectMyTimesheetEntriesInitialValues, selectMyTimesheetEntryUuid],
    (myTimesheetEntriesInitialValuesState, myTimesheetEntryUuidProp) =>
        get(myTimesheetEntriesInitialValuesState, myTimesheetEntryUuidProp)
);

const selectMyTimesheetEntryValue = createDeepEqualSelector(
    [selectMyTimesheetEntriesValues, selectMyTimesheetEntryUuid],
    (myTimesheetEntriesValuesState, myTimesheetEntryUuidProp) =>
        get(myTimesheetEntriesValuesState, myTimesheetEntryUuidProp)
);

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectMyTimesheetIsSaved = () =>
    createDeepEqualSelector(
        [selectMyTimesheetEntryInitialValue, selectMyTimesheetEntryValue],
        (
            timesheetMyEntryInitialValueState,
            timesheetMyEntryValueState
        ): boolean =>
            (timesheetMyEntryValueState &&
                timesheetMyEntryValueState.lastReviewIsApproved) ||
            isEqual(
                timesheetMyEntryInitialValueState,
                timesheetMyEntryValueState
            )
    );

const selectMyTimesheetEntry = createDeepEqualSelector(
    [selectMyTimesheetEntries, selectMyTimesheetEntryUuid],
    (myTimesheetEntriesState, myTimesheetEntryUuidProp) =>
        get(myTimesheetEntriesState, myTimesheetEntryUuidProp)
);

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectMyTimesheetEntryDate = () =>
    createDeepEqualSelector(selectMyTimesheetEntry, (timesheetMyEntryState) =>
        get(timesheetMyEntryState, 'date')
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectMyTimesheetEntryLastReviewIsApproved = () =>
    createDeepEqualSelector(selectMyTimesheetEntry, (timesheetMyEntryState) =>
        get(timesheetMyEntryState, 'lastReviewIsApproved')
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectMyTimesheetEntryTotalPaidMinutes = () =>
    createDeepEqualSelector(selectMyTimesheetEntry, (timesheetMyEntryState) =>
        get(timesheetMyEntryState, 'totalPaidMinutes')
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectMyTimesheetEntryWorkTypeId = () =>
    createDeepEqualSelector(selectMyTimesheetEntry, (timesheetMyEntryState) =>
        get(timesheetMyEntryState, 'workTypeId')
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectMyTimesheetEntryWorkTypeName = () =>
    createDeepEqualSelector(selectMyTimesheetEntry, (timesheetMyEntryState) =>
        get(timesheetMyEntryState, 'workTypeName')
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectMyTimesheetEntryStartTime = () =>
    createDeepEqualSelector(selectMyTimesheetEntry, (timesheetMyEntryState) =>
        get(timesheetMyEntryState, 'startTime')
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectMyTimesheetEntryEndTime = () =>
    createDeepEqualSelector(selectMyTimesheetEntry, (timesheetMyEntryState) =>
        get(timesheetMyEntryState, 'endTime')
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectMyTimesheetEntryEntityId = () =>
    createDeepEqualSelector(selectMyTimesheetEntry, (timesheetMyEntryState) =>
        get(timesheetMyEntryState, 'entityId')
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectMyTimesheetEffectiveRate = () =>
    createDeepEqualSelector(selectMyTimesheetEntry, (timesheetMyEntryState) =>
        get(timesheetMyEntryState, 'effectiveRate')
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectMyTimesheetEarnings = () =>
    createDeepEqualSelector(selectMyTimesheetEntry, (timesheetMyEntryState) =>
        get(timesheetMyEntryState, 'earnings')
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectMyTimesheetPaidBreak = () =>
    createDeepEqualSelector(selectMyTimesheetEntry, (timesheetMyEntryState) =>
        get(timesheetMyEntryState, 'paidBreakDollars')
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectMyTimesheetWorkTypeIsPieceRate = () =>
    createDeepEqualSelector(selectMyTimesheetEntry, (timesheetMyEntryState) =>
        get(timesheetMyEntryState, 'workTypeIsPieceRate')
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectMyTimesheetEntryTimesheetId = (): any =>
    createDeepEqualSelector(selectMyTimesheetEntry, (timesheetMyEntryState) =>
        get(timesheetMyEntryState, 'timesheetId')
    );

const selectMyTimesheetEntryCreatedByUserId = createDeepEqualSelector(
    selectMyTimesheetEntry,
    (timesheetMyEntryState) => get(timesheetMyEntryState, 'createdByUserId')
);

const makeSelectMyTimesheetEntryCreatedByUserId = (): typeof selectMyTimesheetEntryCreatedByUserId =>
    selectMyTimesheetEntryCreatedByUserId;

const selectMyTimesheetEntryLastReviewedAt = createDeepEqualSelector(
    selectMyTimesheetEntry,
    (timesheetMyEntryState) => get(timesheetMyEntryState, 'lastReviewedAt')
);

const makeSelectMyTimesheetEntryLastReviewedAt = (): typeof selectMyTimesheetEntryLastReviewedAt =>
    selectMyTimesheetEntryLastReviewedAt;

const selectMyTimesheetEntryGroupId = createDeepEqualSelector(
    selectMyTimesheetEntry,
    (timesheetMyEntryState) => get(timesheetMyEntryState, 'groupId')
);

const makeSelectMyTimesheetEntryGroupId = (): typeof selectMyTimesheetEntryGroupId =>
    selectMyTimesheetEntryGroupId;

export {
    selectMyTimesheetEntriesInitialValues,
    selectMyTimesheetEntriesValues,
    selectMyTimesheetEntryUuid,
    selectMyTimesheetEntries,
    selectMyTimesheetEntriesArray,
    selectMyTimesheetEntriesGroupedByDate,
    makeSelectMyTimesheetEntriesByDate,
    selectMyTimesheetEntriesDateTotals,
    makeSelectMyTimesheetEntriesDateTotal,
    selectMyTimesheetEntryInitialValue,
    makeSelectMyTimesheetIsSaved,
    selectUnsavedMyTimesheetEntriesExist,
    selectUnsavedMyTimesheetEntries,
    selectMyTimesheetEntryValue,
    makeSelectMyTimesheetEntryDate,
    makeSelectMyTimesheetEntryLastReviewIsApproved,
    makeSelectMyTimesheetEntryTotalPaidMinutes,
    makeSelectMyTimesheetEntryWorkTypeId,
    makeSelectMyTimesheetEntryWorkTypeName,
    makeSelectMyTimesheetEntryStartTime,
    makeSelectMyTimesheetEntryEndTime,
    makeSelectMyTimesheetEntryEntityId,
    makeSelectMyTimesheetEffectiveRate,
    makeSelectMyTimesheetEarnings,
    makeSelectMyTimesheetPaidBreak,
    makeSelectMyTimesheetWorkTypeIsPieceRate,
    makeSelectMyTimesheetEntryTimesheetId,
    makeSelectMyTimesheetEntryCreatedByUserId,
    makeSelectMyTimesheetEntryLastReviewedAt,
    makeSelectMyTimesheetEntryGroupId
};
